import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { schemeAPI } from '../../services/api';
import { openExternalWithNotice } from '../../utils/externalLinks';
import { formatMarathiNumber, toMarathiDigits } from '../../utils/marathi';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import './SchemesPage.css';

const LEVEL_META = {
  central: { label: 'केंद्र योजना', short: 'केंद्र', color: '#1d67d2', bg: '#eaf3ff' },
  state: { label: 'राज्य योजना', short: 'राज्य', color: '#8b45d8', bg: '#f3eaff' },
  district: { label: 'जिल्हा योजना', short: 'जिल्हा', color: '#f97316', bg: '#fff0e5' },
  local: { label: 'ग्रामपंचायत योजना', short: 'ग्रामपंचायत', color: '#1f9d55', bg: '#e9f8ef' },
};

const CATEGORY_META = {
  housing: { label: 'घरकुल', icon: 'home', color: '#2563eb', bg: '#edf4ff' },
  financial: { label: 'आर्थिक', icon: 'coins', color: '#9b59b6', bg: '#f4eafd' },
  health: { label: 'आरोग्य', icon: 'heart', color: '#ef476f', bg: '#fff0f4' },
  education: { label: 'शिक्षण', icon: 'cap', color: '#7c3aed', bg: '#f1eaff' },
  elderly: { label: 'ज्येष्ठ नागरिक', icon: 'user', color: '#0ea5e9', bg: '#e9f7ff' },
  agriculture: { label: 'कृषी', icon: 'leaf', color: '#22a447', bg: '#eaf8ef' },
  women: { label: 'महिला व बालकल्याण', icon: 'woman', color: '#d9467a', bg: '#fff0f6' },
  other: { label: 'इतर', icon: 'grid', color: '#64748b', bg: '#eef2f6' },
};

function Icon({ name, size = 22 }) {
  const paths = {
    home: <><path d="m3 11 9-8 9 8" /><path d="M5 10v11h14V10M9 21v-6h6v6" /></>,
    coins: <><ellipse cx="12" cy="6" rx="7" ry="3" /><path d="M5 6v6c0 1.7 3.1 3 7 3s7-1.3 7-3V6M5 12v6c0 1.7 3.1 3 7 3s7-1.3 7-3v-6" /></>,
    heart: <><path d="M20.8 4.6a5.4 5.4 0 0 0-7.6 0L12 5.8l-1.2-1.2a5.4 5.4 0 1 0-7.6 7.6L12 21l8.8-8.8a5.4 5.4 0 0 0 0-7.6Z" /><path d="M7 12h3l1-3 2 6 1-3h3" /></>,
    cap: <><path d="m3 8 9-5 9 5-9 5-9-5Z" /><path d="M7 10v5c3 2 7 2 10 0v-5" /></>,
    user: <><circle cx="12" cy="7" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></>,
    leaf: <><path d="M5 21c8 0 14-6 14-14V4h-3C8 4 3 9 3 17v4h2Z" /><path d="M5 19c4-5 8-8 14-12" /></>,
    woman: <><circle cx="12" cy="7" r="4" /><path d="M12 11v10M8 15h8M9 21h6" /></>,
    grid: <><rect x="4" y="4" width="6" height="6" rx="1" /><rect x="14" y="4" width="6" height="6" rx="1" /><rect x="4" y="14" width="6" height="6" rx="1" /><rect x="14" y="14" width="6" height="6" rx="1" /></>,
    search: <><circle cx="11" cy="11" r="7" /><path d="m20 20-4-4" /></>,
    arrow: <path d="M5 12h14M13 5l7 7-7 7" />,
    check: <path d="m5 12 4 4L19 6" />,
    file: <><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9Z" /><path d="M14 3v6h6M8 13h8M8 17h5" /></>,
    bell: <><path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" /><path d="M10 21h4" /></>,
    reset: <><path d="M3 12a9 9 0 1 0 3-6.7" /><path d="M3 4v5h5" /></>,
    list: <><path d="M8 6h13M8 12h13M8 18h13" /><path d="M3 6h.01M3 12h.01M3 18h.01" /></>,
    card: <><rect x="4" y="4" width="7" height="7" rx="1" /><rect x="13" y="4" width="7" height="7" rx="1" /><rect x="4" y="13" width="7" height="7" rx="1" /><rect x="13" y="13" width="7" height="7" rx="1" /></>,
    external: <><path d="M15 3h6v6" /><path d="M10 14 21 3" /><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /></>,
  };
  return <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.85" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths[name] || paths.grid}</svg>;
}

const levelMeta = level => LEVEL_META[level] || { label: level || 'योजना', short: level || 'योजना', color: '#64748b', bg: '#eef2f6' };
const categoryMeta = category => CATEGORY_META[category] || CATEGORY_META.other;
const formatDate = value => value ? toMarathiDigits(new Date(value).toLocaleDateString('mr-IN', { day: '2-digit', month: 'short', year: 'numeric' })) : 'सतत सुरू';
const externalUrl = value => {
  const trimmed = String(value || '').trim();
  if (!trimmed) return '';
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

const parseJsonValue = value => {
  let current = value;
  for (let i = 0; i < 4; i += 1) {
    if (typeof current !== 'string') return current;
    const trimmed = current.trim();
    if (!trimmed) return '';
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed === current) return current;
      current = parsed;
    } catch {
      return current;
    }
  }
  return current;
};

const cleanDisplayText = value => String(value || '')
  .trim()
  .replace(/^[[\]{}\s'"]+|[[\]{}\s'"]+$/g, '')
  .replace(/^"(.*)"$/, '$1')
  .trim();

const itemText = item => {
  const parsed = parseJsonValue(item);
  if (!parsed) return '';
  if (typeof parsed === 'string') {
    const text = cleanDisplayText(parsed);
    const fieldMatch = text.match(/"(?:name|document_name|label|title|text|question)"\s*:\s*"([^"]+)"/i);
    return toMarathiDigits(fieldMatch?.[1] || text);
  }
  if (Array.isArray(parsed)) return parsed.map(itemText).filter(Boolean).join(', ');
  return toMarathiDigits(parsed.name || parsed.document_name || parsed.label || parsed.title || parsed.text || parsed.question || '');
};

const parseStructuredList = value => {
  const parsed = parseJsonValue(value);
  if (!parsed) return [];

  if (Array.isArray(parsed)) {
    const joined = parsed.every(item => typeof item === 'string') ? parsed.join(',') : '';
    const parsedJoined = joined ? parseJsonValue(joined) : null;
    if (Array.isArray(parsedJoined)) return parseStructuredList(parsedJoined);
    return parsed.flatMap(item => parseStructuredList(item));
  }

  if (typeof parsed === 'object') {
    if (parsed.name || parsed.document_name || parsed.label || parsed.title || parsed.text || parsed.question) return [parsed];
    return Object.values(parsed).flatMap(item => parseStructuredList(item));
  }

  const text = String(parsed || '').trim();
  if (!text) return [];
  const parsedAgain = parseJsonValue(text);
  if (parsedAgain !== text) return parseStructuredList(parsedAgain);
  return text.split(/\r?\n/).map(item => item.trim()).filter(Boolean);
};

const normalizeDocumentRequirements = value => {
  const seen = new Set();
  return parseStructuredList(value).map(item => {
    const parsed = parseJsonValue(item);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return {
        name: itemText(parsed),
        is_required: (parsed.is_required ?? parsed.required ?? true) !== false,
      };
    }
    return { name: itemText(parsed), is_required: true };
  }).filter(item => {
    const key = item.name.toLowerCase();
    if (!item.name || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export default function SchemesPage() {
  const { tenantSlug } = useParams();
  const [schemes, setSchemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [level, setLevel] = useState('all');
  const [category, setCategory] = useState('all');
  const [view, setView] = useState('grid');

  useEffect(() => {
    setLoading(true);
    schemeAPI.getAll(tenantSlug)
      .then(res => setSchemes(res.data.schemes || []))
      .catch(() => setError('योजना माहिती लोड करता आली नाही.'))
      .finally(() => setLoading(false));
  }, [tenantSlug]);

  const categories = useMemo(() => [...new Set(schemes.map(s => s.category).filter(Boolean))], [schemes]);
  const levels = useMemo(() => [...new Set(schemes.map(s => s.scheme_level).filter(Boolean))], [schemes]);
  const filtered = useMemo(() => schemes.filter(scheme => {
    const query = search.trim().toLowerCase();
    const text = [scheme.name, scheme.short_name, scheme.description, scheme.benefit_details, scheme.benefit_amount, scheme.category].filter(Boolean).join(' ').toLowerCase();
    return (!query || text.includes(query)) && (level === 'all' || scheme.scheme_level === level) && (category === 'all' || scheme.category === category);
  }), [schemes, search, level, category]);
  const resetFilters = () => {
    setSearch('');
    setLevel('all');
    setCategory('all');
  };

  const stats = [
    { label: 'एकूण योजना', value: schemes.length, icon: 'file', tone: 'blue' },
    { label: 'केंद्र योजना', value: schemes.filter(s => s.scheme_level === 'central').length, icon: 'grid', tone: 'green' },
    { label: 'राज्य योजना', value: schemes.filter(s => s.scheme_level === 'state').length, icon: 'woman', tone: 'orange' },
    { label: 'ग्रामपंचायत योजना', value: schemes.filter(s => s.scheme_level === 'local').length, icon: 'home', tone: 'purple' },
  ];

  return (
    <div className="schemes-page redesigned">
      <section className="schemes-modern-hero">
        <div>
          <nav><Link to={`/gp/${tenantSlug}`}>मुख्यपृष्ठ</Link><span>›</span><strong>योजना</strong></nav>
          <h1>योजना</h1>
          <p>केंद्र, राज्य आणि ग्रामपंचायत मार्फत राबविल्या जाणाऱ्या कल्याणकारी योजनांची माहिती व लाभ मिळवा.</p>
        </div>
      </section>

      <div className="schemes-layout">
        <aside className="schemes-left-rail">
          <section className="schemes-filter-card">
            <div className="schemes-filter-head">
              <h2>शोध व फिल्टर</h2>
              <button type="button" onClick={resetFilters}><Icon name="reset" size={15} /> रीसेट करा</button>
            </div>
            <label className="scheme-search-box">
              <Icon name="search" size={18} />
              <input value={search} onChange={event => setSearch(event.target.value)} placeholder="योजना नावाने शोधा..." />
            </label>

            <h3>योजना प्रकार</h3>
            {levels.map(item => {
              const count = schemes.filter(s => s.scheme_level === item).length;
              return (
                <label className="scheme-check-row" key={item}>
                  <input type="radio" checked={level === item} onChange={() => setLevel(item)} />
                  <span>{levelMeta(item).label}</span><b>{formatMarathiNumber(count)}</b>
                </label>
              );
            })}
            <label className="scheme-check-row">
              <input type="radio" checked={level === 'all'} onChange={() => setLevel('all')} />
              <span>सर्व योजना</span><b>{formatMarathiNumber(schemes.length)}</b>
            </label>

            <h3>लाभार्थी / विभाग</h3>
            <select value={category} onChange={event => setCategory(event.target.value)}>
              <option value="all">सर्व विभाग</option>
              {categories.map(item => <option key={item} value={item}>{categoryMeta(item).label}</option>)}
            </select>
          </section>

          <section className="schemes-help-card">
            <Icon name="bell" size={36} />
            <h2>मदत हवी आहे?</h2>
            <p>योजना, अर्ज प्रक्रिया किंवा कागदपत्रांसाठी ग्रामपंचायत कार्यालयाशी संपर्क साधा.</p>
            <Link to={`/gp/${tenantSlug}/contact`}>संपर्क साधा</Link>
          </section>

        </aside>

        <main className="schemes-main">
          <section className="schemes-stats-grid">
            {stats.map(stat => (
              <article className={`scheme-stat ${stat.tone}`} key={stat.label}>
                <span><Icon name={stat.icon} /></span>
                <p><strong>{formatMarathiNumber(stat.value)}</strong><small>{stat.label}</small></p>
              </article>
            ))}
          </section>

          <section className="schemes-content-head">
            <div>
              <h2>सर्व योजना</h2>
              <span>एकूण {formatMarathiNumber(filtered.length)} योजना</span>
            </div>
            <div className="schemes-actions">
              <select value={level} onChange={event => setLevel(event.target.value)}>
                <option value="all">सर्व स्तर</option>
                {levels.map(item => <option key={item} value={item}>{levelMeta(item).label}</option>)}
              </select>
              <button type="button" className={view === 'grid' ? 'active' : ''} onClick={() => setView('grid')} aria-label="Grid view"><Icon name="card" size={18} /></button>
              <button type="button" className={view === 'list' ? 'active' : ''} onClick={() => setView('list')} aria-label="List view"><Icon name="list" size={18} /></button>
            </div>
          </section>

          {loading ? (
            <LoadingSpinner message="शासकीय व स्थानिक योजना लोड होत आहेत..." />
          ) : error ? (
            <div className="schemes-state error">{error}</div>
          ) : filtered.length === 0 ? (
            <div className="schemes-state">निवडलेल्या फिल्टरसाठी योजना उपलब्ध नाहीत.</div>
          ) : (
            <section className={`schemes-modern-grid ${view === 'list' ? 'list-view' : ''}`}>
              {filtered.map(scheme => {
                const level = levelMeta(scheme.scheme_level);
                const category = categoryMeta(scheme.category);
                const docs = normalizeDocumentRequirements(scheme.required_documents);
                const eligibility = parseStructuredList(scheme.eligibility_criteria);
                const portalUrl = externalUrl(scheme.portal_url);
                const canApplyOnGp = Boolean(scheme.tenant_id) && scheme.scheme_level === 'local' && !portalUrl;
                return (
                  <article className="scheme-modern-card" key={scheme.id} style={{ '--scheme-color': category.color, '--scheme-bg': category.bg, '--level-color': level.color, '--level-bg': level.bg }}>
                    <span className="scheme-modern-icon"><Icon name={category.icon} size={32} /></span>
                    <div className="scheme-modern-body">
                      <div className="scheme-modern-title">
                        <div>
                          <span className="scheme-category-label">{category.label}</span>
                          <h3>{scheme.name}</h3>
                        </div>
                        <span className="scheme-level-label">{level.short}</span>
                      </div>
                      <p>{scheme.description || 'या योजनेची माहिती लवकरच उपलब्ध होईल.'}</p>
                      {eligibility.length > 0 && (
                        <div className="scheme-key-eligibility">
                          <Icon name="check" size={16} />
                          <span><strong>प्रमुख पात्रता:</strong> {itemText(eligibility[0])}</span>
                        </div>
                      )}
                      <div className="scheme-benefit-highlight">
                        <span className="scheme-benefit-icon">₹</span>
                        <p><small>योजनेचा प्रमुख लाभ</small><strong>{toMarathiDigits(scheme.benefit_amount || scheme.benefit_details || 'माहिती उपलब्ध नाही')}</strong></p>
                      </div>
                      <div className="scheme-card-meta">
                        <div>
                          <span className="scheme-summary-icon"><Icon name="file" size={18} /></span>
                          <p><small>कागदपत्रे</small><strong>{docs.length > 0 ? `${formatMarathiNumber(docs.length)} आवश्यक` : 'तपशील पहा'}</strong></p>
                        </div>
                        <div>
                          <span className="scheme-summary-icon"><Icon name="user" size={18} /></span>
                          <p><small>पात्रता</small><strong>{eligibility.length > 0 ? `${formatMarathiNumber(eligibility.length)} निकष` : category.label}</strong></p>
                        </div>
                        <div>
                          <span className="scheme-summary-icon"><Icon name="calendar" size={18} /></span>
                          <p><small>वैधता</small><strong>{scheme.end_date ? formatDate(scheme.end_date) : 'सतत चालू'}</strong></p>
                        </div>
                      </div>
                      <div className="scheme-modern-actions">
                        <Link to={`/gp/${tenantSlug}/schemes/${scheme.id}`}>अधिक माहिती <Icon name="arrow" size={15} /></Link>
                        {canApplyOnGp ? (
                          <Link className="scheme-action-primary" to={`/gp/${tenantSlug}/schemes/${scheme.id}#application`}>
                            अर्ज करा <Icon name="arrow" size={14} />
                          </Link>
                        ) : portalUrl ? (
                          <a href={portalUrl} target="_blank" rel="noreferrer" onClick={event => openExternalWithNotice(event, portalUrl)}>
                            ऑनलाइन लिंक / वेबसाइट <Icon name="external" size={14} />
                          </a>
                        ) : (
                          <Link className="scheme-action-muted" to={`/gp/${tenantSlug}/schemes/${scheme.id}#application`}>
                            अर्ज प्रक्रिया पहा <Icon name="arrow" size={14} />
                          </Link>
                        )}
                      </div>
                    </div>
                  </article>
                );
              })}
            </section>
          )}
        </main>

      </div>
    </div>
  );
}
